import { useCallback, useEffect, useState } from 'react'
import { Chip, CircularProgress, IconButton, Stack, Typography } from '@mui/material'
import { Close, Done, SettingsBackupRestore } from '@mui/icons-material'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { apiDelete, apiGet, apiPut } from '../../generic/Api_Functions'
import { subscribableCatalog } from '../../maintenance/alarms&notifications/alarmSections'
import AlertUI from '../../generic/AlertUI'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import AlarmSubscriptionTypes from './AlarmSubscriptionTypes'
import AlarmSubscriptionJobFilter from './AlarmSubscriptionJobFilter'

/**
 * Manages the alarm subscriptions of ONE subject: a profile or a user.
 *
 * D11/CA7 — there is no catalogue screen of its own. This dialog is opened from the row of a profile
 * in the Profiles screen and from the row of a user in the Users screen, and it is the SAME component
 * for both because Sun answers both subjects with the same shape
 * (see `contrato-jupiter-sun.md` §2 and §3 in the story folder).
 *
 * DN-6 — no new permission: whoever can administer profiles/users can administer their subscriptions.
 *
 * Mould: `configuration/profiles/ProfilePermission.jsx`. Group→type maps 1:1 onto
 * permission→sub-permission: parent/child accordion and one toggle per row that persists instantly,
 * with NO save button.
 *
 * S122 — the front NEVER resolves the precedence of RN12. It asks Sun for the effective state and
 * paints it verbatim; `own` / `inherited` / `effective` all arrive already decided.
 */

export const subscriptionResource = (subjectType, subjectId) =>
  'alarmSubscriptions/' + (subjectType === 'USER' ? 'user' : 'profile') + '/' + subjectId

// A scope the server did not mention yet: no decision anywhere, so nothing is received (RN11).
const UNDECIDED_SCOPE = { own: '', inherited: '', effective: 'UNSUBSCRIBED', acceptsJobFilter: false, rules: [] }

const EMPTY_FILTER = { clients: [], technologies: [], devices: [] }

const AlarmSubscriptionDialog = ({ subjectType, subjectId, subjectName }) => {
  const { t_ } = useSmartTranslation()
  const [AlertElement, showAlert] = AlertUI()
  const [catalog, setCatalog] = useState([])
  const [scopes, setScopes] = useState([])
  const [filterOptions, setFilterOptions] = useState(EMPTY_FILTER)
  const [loaded, setLoaded] = useState(false)
  const [pendingUnsubscribe, setPendingUnsubscribe] = useState({ open: false, level: '', code: '' })

  const resource = subscriptionResource(subjectType, subjectId)

  useEffect(() => {
    let mounted = true

    const load = async () => {
      // The three filter sources are homogeneous `{id, name}` basic-data lists, the same ones the
      // Users screen already uses for its clients multi-select.
      const [loadedCatalog, loadedState, clients, technologies, devices] = await Promise.all([
        apiGet('alarmCatalog'),
        apiGet(resource),
        apiGet('clientsBasicData'),
        apiGet('technologiesBasicData'),
        apiGet('devicesBasicData')
      ])

      if (!mounted) {
        return
      }

      // Story 162: automation alarms are not subscribed to, so their group and types are not offered.
      setCatalog(subscribableCatalog(loadedCatalog))
      setScopes((loadedState && loadedState.scopes) || [])
      setFilterOptions({
        clients: clients || [],
        technologies: technologies || [],
        devices: devices || []
      })
      setLoaded(true)
    }

    load()

    return () => {
      mounted = false
    }
  }, [resource])

  // Pure lookup of what Sun already decided. No precedence is computed here (S122).
  const stateOf = useCallback((level, code) => {
    const found = scopes.find((item) => item.scope.level === level && item.scope.code === code)
    return found || UNDECIDED_SCOPE
  }, [scopes])

  // "El diálogo refleja el estado devuelto por el servidor tras persistir": the dialog never keeps an
  // optimistic state of its own, so it can never show a rule the server does not have.
  const applyServerState = (updated) => {
    setScopes((updated && updated.scopes) || [])
  }

  // S124: if the server rejects the write, the dialog shows the error and keeps the state the server
  // does have. Since the state is only ever replaced with a server response, a rejection simply
  // leaves the previous one standing — the toggle can never sit in a state Sun never accepted.
  const persist = async (call) => {
    try {
      applyServerState(await call())
    } catch (error) {
      showAlert({ title: t_('Error saving the subscription.'), message: error.message, severity: 'error' })
    }
  }

  const unsubscribeScope = async (level, code) => {
    await persist(() => apiPut(resource, { scope: { level, code }, decision: 'UNSUBSCRIBED' }))
  }

  const toggleDecision = async (level, code) => {
    const current = stateOf(level, code)
    const decision = current.effective === 'SUBSCRIBED' ? 'UNSUBSCRIBED' : 'SUBSCRIBED'

    // S150: unsubscribing a filterable type is a DIFFERENT act from removing one of its rules. It
    // leaves a single "no suscrito" decision with no filter at all (R2), so it takes the subject's own
    // filter rules with it — and on a user it also switches off everything inherited for that type.
    // Destructive and not obvious from the toggle, so it is confirmed first.
    if (decision === 'UNSUBSCRIBED' && current.acceptsJobFilter && (current.rules || []).length > 0) {
      setPendingUnsubscribe({ open: true, level, code })
      return
    }

    // No `filter` key at all where the scope does not admit one (RN15/E5).
    await persist(() => apiPut(resource, { scope: { level, code }, decision }))
  }

  const closePendingUnsubscribe = () => setPendingUnsubscribe({ open: false, level: '', code: '' })

  const confirmPendingUnsubscribe = async () => {
    const { level, code } = pendingUnsubscribe
    closePendingUnsubscribe()
    await unsubscribeScope(level, code)
  }

  // CU2/A3 and S121: removing the own rule altogether. On a user that means going back to inheriting
  // from the profile; on a profile it simply means no decision, i.e. not receiving (RN11).
  const clearDecision = async (level, code) => {
    await persist(() => apiDelete(resource, { scope: { level, code } }))
  }

  // D18 — one more rule for the same type, born with the empty filter (all jobs, RN16).
  const addJobRule = async (code) => {
    await persist(() => apiPut(resource, {
      scope: { level: 'TYPE', code },
      decision: 'SUBSCRIBED',
      filter: EMPTY_FILTER
    }))
  }

  // `ruleId` present ⇒ only that rule's filter changes; the others are untouched (S149).
  const changeJobRule = async (code, ruleId, filter) => {
    await persist(() => apiPut(resource, {
      scope: { level: 'TYPE', code },
      decision: 'SUBSCRIBED',
      ruleId,
      filter
    }))
  }

  const removeJobRule = async (code, ruleId) => {
    await persist(() => apiDelete(resource, { scope: { level: 'TYPE', code }, ruleId }))
  }

  // RN15: only the two job types get a filter, and only once the scope is actually subscribed —
  // a "no suscrito" decision never carries one (R2).
  const renderJobFilter = (type) => {
    const current = stateOf('TYPE', type.code)

    if (!type.acceptsJobFilter || current.effective !== 'SUBSCRIBED') {
      return null
    }

    return (
      <AlarmSubscriptionJobFilter
        typeCode={type.code}
        rules={current.rules || []}
        options={filterOptions}
        onAddRule={() => addJobRule(type.code)}
        onChangeRule={(ruleId, filter) => changeJobRule(type.code, ruleId, filter)}
        onRemoveRule={(ruleId) => removeJobRule(type.code, ruleId)}
      />
    )
  }

  const renderToggle = (level, code) => {
    const current = stateOf(level, code)
    const subscribed = current.effective === 'SUBSCRIBED'

    return (
      <IconButton
        aria-label={'subscription-toggle-' + level + '-' + code}
        onClick={() => toggleDecision(level, code)}
      >
        {subscribed ? <Done color='primary' /> : <Close color='error' />}
      </IconButton>
    )
  }

  // S121: the three situations a USER row can be in, told apart at a glance. An own decision — of
  // either sign — is shown as such; no own decision means it comes from the profile (RN13). The
  // explicit "unsubscribed" is a DATA POINT, not the absence of one: without it CA4 could not
  // switch off what the profile grants.
  const renderOrigin = (level, code) => {
    const current = stateOf(level, code)
    const hasOwnDecision = current.own !== ''

    const label = hasOwnDecision
      ? (current.own === 'SUBSCRIBED' ? 'Subscribed' : 'Not subscribed')
      : 'Inherited from profile'

    return (
      <Stack direction='row' spacing={1} alignItems='center' justifyContent='center'>
        <Chip
          data-testid={'subscription-origin-' + level + '-' + code}
          label={t_(label)}
          size='small'
          variant={hasOwnDecision ? 'filled' : 'outlined'}
          color={current.own === 'SUBSCRIBED' ? 'primary' : (current.own === 'UNSUBSCRIBED' ? 'error' : 'default')}
        />
        {hasOwnDecision
          ? (
            <IconButton
              aria-label={'subscription-inherit-' + level + '-' + code}
              title={t_('Back to inherited')}
              onClick={() => clearDecision(level, code)}
            >
              <SettingsBackupRestore fontSize='small' />
            </IconButton>
            )
          : null}
      </Stack>
    )
  }

  // A profile has nobody to inherit from (RN13), so the whole origin/back-to-inherited affordance is
  // meaningless there and is not rendered.
  const showInheritance = subjectType === 'USER'

  const columns = [
    {
      // P8/S126: the stable code Sun sends IS the translation key. The alarm's own `code` and
      // `description` keep being painted raw elsewhere — that debt is deliberately kept.
      name: t_('Group'),
      field: 'code',
      render: (row) => t_(row.code)
    },
    {
      name: t_('Subscribed'),
      field: 'effective',
      render: (row) => renderToggle('GROUP', row.code)
    },
    ...(showInheritance
      ? [{
          name: t_('Origin'),
          field: 'own',
          render: (row) => renderOrigin('GROUP', row.code)
        }]
      : [])
  ]

  const actions = {
    accordion: {
      // Deliberately NOT hidden for a group without types: INTEGRATIONS expands empty and stays
      // subscribable as a group (RN7, S120).
      render: (row) => (
        <AlarmSubscriptionTypes
          types={row.types || []}
          renderToggle={renderToggle}
          renderOrigin={showInheritance ? renderOrigin : undefined}
          renderJobFilter={renderJobFilter}
        />
      )
    }
  }

  if (!loaded) {
    return <CircularProgress />
  }

  return (
    <>
      {AlertElement}
      <SmartDialog
        setOpen={pendingUnsubscribe.open}
        title='default'
        accept
        cancel
        acceptCallback={confirmPendingUnsubscribe}
        cancelCallback={closePendingUnsubscribe}
        closeCallback={closePendingUnsubscribe}
        renderComponent={
          <Stack spacing={1}>
            <Typography>{t_('Unsubscribing this type withdraws its own filter rules')}</Typography>
            {showInheritance
              ? <Typography>{t_('It also switches off everything inherited from the profile for this type')}</Typography>
              : null}
          </Stack>
        }
      />
      <Typography variant='h6'>{t_('Alarm subscriptions') + ' — ' + subjectName}</Typography>
      <SmartMateriaUITable
        columns={columns}
        // SmartMateriaUITable keys accordion visibility on `row.id`, so every group row needs a
        // stable one of its own — without it all seven groups would expand and collapse together.
        // The catalogue code IS the stable identity (R1: the catalogue is closed and seeded).
        dataFetch={() => catalog.map((group) => ({ ...group, id: group.code }))}
        actions={actions}
        toolbarDisplay='none'
        dense
      />
    </>
  )
}

export default AlarmSubscriptionDialog
